import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { verifyAccount, resendVerification, login, setVerifyError } from '../app/redux/slices/authSlice';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { RootState, AppDispatch } from '../app/redux/store';
import { useTranslation } from 'react-i18next';
import { logger } from '../app/utils/logger';
import LanguageSwitcher from '../app/components/LanguageSwitcher';

export default function VerificationPage() {
  const { t } = useTranslation(['auth', 'common']);
  const [verificationCode, setVerificationCode] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const email = searchParams.get('email') || '';
  const password = searchParams.get('password') || '';
  
  const { verifyError, loading } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // Clear any previous verification errors when component mounts
    dispatch(setVerifyError(''));
  }, [dispatch]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!verificationCode.trim()) {
      dispatch(setVerifyError(t('auth:codeRequired')));
      return;
    }

    try {
      await dispatch(verifyAccount({
        email: email,
        code: verificationCode
      })).unwrap();
      
      // Show success modal
      setShowSuccessModal(true);
      
      // After 2 seconds, proceed to next step
      setTimeout(async () => {
        setShowSuccessModal(false);
        
        // After successful verification, automatically log the user in
        if (email && password) {
          await dispatch(login({
            username: email,
            password: password
          })).unwrap();
          navigate('/dashboard');
        } else {
          navigate('/login?verified=true');
        }
      }, 2000);
    } catch (error) {
      logger.error('Verification failed:', error);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      dispatch(setVerifyError(t('common:error')));
      return;
    }

    try {
      await dispatch(resendVerification(email)).unwrap();
      dispatch(setVerifyError(t('auth:emailSentTitle')));
    } catch (error) {
      logger.error('Failed to resend code:', error);
    }
  };

  const goBack = () => {
    navigate('/register');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('auth:verificationTitle')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-900">
            {t('auth:checkInboxSpam')}{' '}
            <span className="font-medium text-indigo-600">{email}</span>
          </p>
        </div>
        
        {/* Language Switcher */}
        <div className="flex justify-center">
          <LanguageSwitcher />
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleVerify}>
          {/* Error Display */}
          {verifyError && (
            <div className={`border rounded-md p-4 ${
              verifyError.includes('successfully') 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex">
                <div className="ml-3">
                  <h3 className={`text-sm font-medium ${
                    verifyError.includes('successfully') 
                      ? 'text-green-800' 
                      : 'text-red-800'
                  }`}>
                    {verifyError.includes('successfully') ? t('common:success') : t('common:error')}
                  </h3>
                  <div className={`mt-2 text-sm ${
                    verifyError.includes('successfully') 
                      ? 'text-green-700' 
                      : 'text-red-700'
                  }`}>
                    {verifyError}
                  </div>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => dispatch(setVerifyError(''))}
                      className={`text-sm rounded-md px-2 py-1 transition-colors duration-200 focus:outline-none focus:ring-2 ${
                        verifyError.includes('successfully')
                          ? 'bg-green-50 text-green-800 hover:bg-green-100 focus:ring-green-500'
                          : 'bg-red-50 text-red-800 hover:bg-red-100 focus:ring-red-500'
                      }`}
                    >
                      {t('auth:dismiss')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-900">
              {t('auth:verificationCode')}
            </label>
            <input
              id="verificationCode"
              name="verificationCode"
              type="text"
              required
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-center text-lg tracking-widest"
              placeholder={t('auth:enterVerificationCode')}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              maxLength={6}
            />
          </div>

          <div className="space-y-4">
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('auth:verifying') : t('auth:verifyButton')}
            </button>

            <button
              type="button"
              onClick={handleResendCode}
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-900 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('auth:resendCode')}
            </button>
          </div>

          <div className="text-center space-y-2">
            <button
              type="button"
              onClick={goBack}
              className="font-medium text-indigo-600 hover:text-indigo-500 text-sm"
            >
              {t('auth:backToLogin')}
            </button>
            <br />
            <Link 
              to="/login" 
              className="font-medium text-gray-900 hover:text-gray-800 text-sm"
            >
              {t('auth:signInNow')}
            </Link>
          </div>
        </form>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-sm w-full mx-4 shadow-xl">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('auth:verificationSuccess')}
              </h3>
              <p className="text-sm text-gray-900 mb-4">
                {t('auth:accountVerified')}
              </p>
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
              </div>
              <p className="text-xs text-gray-800 mt-2">
                {t('common:loading')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
